import { makeStyles } from '@material-ui/core'
import { useFormikContext } from 'formik'
import moment from 'moment'
import PropTypes from 'prop-types'
import { groupBy, sortBy } from 'lodash'
import React from 'react'
import * as Yup from 'yup'

import {
  Form,
  FormikDatePicker,
  FormikFileField,
  FormikToggle,
  Typography,
  fileToBase64,
  mapArrayErrorsToObject,
} from 'src/modules/ui'

const useStyles = makeStyles(theme => ({
  label: {
    marginRight: theme.spacing(2),
  },
  table: {
    borderSpacing: 0,
    '& td': {
      display: 'block',
      paddingBottom: theme.spacing(1),
    },
    [theme.breakpoints.up('tablet')]: {
      '& td': {
        display: 'table-cell',
        paddingBottom: theme.spacing(1),
      },
    },
  },
}))

const DATE_FORMAT = 'YYYY-MM-DD'

export const initialValues = (existingValues, sustainabilityOptions) => {
  const values = sustainabilityOptions.reduce(reduceToObjectSustain, {})

  if (existingValues) {
    existingValues.forEach(({ document, expiry, id }) => {
      values[id] = {
        active: true,
        document,
        expiry: expiry ? moment(expiry, DATE_FORMAT) : null,
      }
    })
  }

  return values
}

const reduceToObjectSustain = (acc, option) => ({
  ...acc,
  [option.id]: {
    active: false,
    document: '',
    expiry: null,
  },
})

export const validationSchema = sustainabilityOptions =>
  Yup.object().shape(
    sustainabilityOptions.reduce(
      (acc, option) => ({
        ...acc,
        [option.id]: Yup.object().shape({
          expiry: Yup.date().typeError('Invalid date').nullable(),
        }),
      }),
      {}
    )
  )

export const parseValuesForRequest = async (
  values,
  sustainabilityOptions,
  categoryId
) => {
  const sustainabilityItems = Object.keys(values)
    .filter(key => values[key].active)
    .filter(id => {
      // Remove any old sustainability options that do not apply to this category.
      // This can happen if a product cateogry is changed - so unlikely

      // If categoryId not passed then we can bypass this
      if (!categoryId) {
        return true
      }

      const option = sustainabilityOptions.find(o => o.id === Number(id))
      if (!option) {
        return false
      }

      const { appliesToProductCategory } = option
      if (appliesToProductCategory.length === 0) {
        return true
      } else {
        return appliesToProductCategory.includes(Number(categoryId))
      }
    })
    .map(id => ({
      sustainabilityId: id,
      expiry: values[id].expiry ? values[id].expiry.format(DATE_FORMAT) : null,
      documentData: values[id].document,
    }))

  const sustainabilityItemsWithDocument = await Promise.all(
    sustainabilityItems.map(async ({ documentData, ...item }) => ({
      ...item,
      documentData: await getFileParam(documentData),
    }))
  )

  return sustainabilityItemsWithDocument
}

export const parseErrorsResponse = (errors, values) => {
  const fieldErrors = mapArrayErrorsToObject(
    errors,
    values,
    'sustainabilityId',
    ({ documentData, ...error }) => ({
      document: documentData,
      ...error,
    })
  )
  return fieldErrors
}

const getFileParam = async file =>
  file instanceof File ? await fileToBase64(file) : file || null

const SustainabilityForm = ({ sustainabilityOptions }) => {
  const classes = useStyles()
  const {
    values: { sustainability, categoryId },
  } = useFormikContext()

  const applicableOptions = sustainabilityOptions.filter(option => {
    const { appliesToProductCategory } = option
    if (appliesToProductCategory.length === 0) {
      return true
    } else {
      return appliesToProductCategory.includes(Number(categoryId))
    }
  })

  const sustainabilityByGroup = groupBy(applicableOptions, a => a.group)
  const sustainabilityGroups = Object.keys(sustainabilityByGroup).map(
    group => ({
      group,
      items: sortBy(sustainabilityByGroup[group], 'name'),
    })
  )

  return (
    <Form>
      <table className={classes.table}>
        <tbody>
          {sustainabilityGroups.map(({ group, items }, idx) => (
            <React.Fragment key={idx}>
              <tr>
                <td colSpan={4}>
                  <Typography variant="h4">{group}</Typography>
                </td>
              </tr>
              {items.map(({ id, name, question }) => (
                <tr key={id}>
                  <td>
                    <Typography className={classes.label}>
                      {question}
                    </Typography>
                  </td>
                  <td>
                    <FormikToggle
                      label="Yes"
                      name={`sustainability.${id}.active`}
                    />
                  </td>
                  {sustainability[id].active && (
                    <>
                      <td>
                        <FormikFileField
                          label="Evidence"
                          name={`sustainability.${id}.document`}
                        />
                      </td>
                      <td>
                        <FormikDatePicker
                          disablePast
                          label="Expiry Date"
                          name={`sustainability.${id}.expiry`}
                          InputLabelProps={{ shrink: true }}
                          placeholder="DD/MM/YYYY"
                        />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </Form>
  )
}

SustainabilityForm.propTypes = {
  sustainabilityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      image: PropTypes.string,
      name: PropTypes.string,
      group: PropTypes.string,
      question: PropTypes.string,
      appliesToSupplier: PropTypes.bool,
      appliesToProduct: PropTypes.bool,
      appliesToProductCategory: PropTypes.array,
    })
  ),
}

export default SustainabilityForm
